import React from 'react';
import { TTransactionHistory } from '../../types/TransactionHistory';
import moment from 'moment';
import cn from '@/utils/cn';
import { DATE_FORMAT_WEEKDAY_TIME } from '@/utils/dateFormats';
import AvatarWithIcon from '../AvatarWithIcon';
import { getTransaction } from '../../utils/getTransaction';
import { getTransactionStatus } from '../../utils/getTransactionStatus';
import { getTransactionDetails } from '../../utils/getTransactionDetails';
import { getTransactionAmount } from '../../utils/getTransactionAmount';
import { getTransactionTypeLabel } from '../../utils/getTransactionTypeLabel';
import Capsule from '@/components/Capsule';
import { isCredit } from '../../utils/isCredit';
import { getAmountSign } from '../../utils/getAmountSign';
import CurrencyFormat from 'react-currency-format';
import { TTransactionGeneralLedgerCode } from '@/context/types/Business';
import ReceiptCell from './ReceiptCell';
import { TCustomer } from '@/context/types/Customer';
import { AccountType } from '@/context/types/Account';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';

const CellClass = cn(['pl-4', 'pr-6', 'py-4']);
const hiddenStyleOnSmallScreens = 'tablet:hidden mobile:hidden';

interface TransactionHistoryTableRowProps {
  index: number;
  customer: TCustomer | undefined;
  accountType: AccountType | undefined;
  transaction: TTransactionHistory;
  hasGLCodes: boolean;
  currentGeneralLedgerCodes: TTransactionGeneralLedgerCode[];
  archivedGeneralLedgerCodes: TTransactionGeneralLedgerCode[];
  onClickDetails: (transaction: TTransactionHistory) => void;
  isSelected?: boolean;
}

const TransactionHistoryTableRow: React.FC<TransactionHistoryTableRowProps> = ({
  index,
  customer,
  transaction,
  accountType,
  hasGLCodes,
  archivedGeneralLedgerCodes,
  currentGeneralLedgerCodes,
  onClickDetails: handleClickDetails,
  isSelected = false
}) => {
  let generalLedgerCode;
  let archived = false;

  generalLedgerCode = currentGeneralLedgerCodes.find(
    (code) => code.code === transaction.details?.generalLedgerCode?.code
  );

  if (!generalLedgerCode) {
    generalLedgerCode = archivedGeneralLedgerCodes.find(
      (code) => code.code === transaction.details?.generalLedgerCode?.code
    );

    archived = true;
  }
  const transactionTitle = getTransaction(transaction);
  const transactionStatus = getTransactionStatus(transaction.data.state);
  const transactionDetails = getTransactionDetails(transaction, accountType);
  const transactionAmount = getTransactionAmount(transaction);

  return (
    <tr
      className={cn(
        'border-b hover:cursor-pointer transition-colors',
        isFeatureEnabled(Feature.SELECTED_ACCOUNT)
          ? isSelected
            ? 'bg-emerge-teal-5'
            : 'bg-white hover:bg-emerge-base-2'
          : 'bg-white hover:bg-emerge-base-2'
      )}
      key={`transaction-${index}`}
      onClick={() => handleClickDetails(transaction)}>
      <td
        id={`transaction-date-${index}`}
        className={cn(CellClass, 'text-xs text-dark-navy-30 font-medium')}>
        {moment(transaction.data.date).format(DATE_FORMAT_WEEKDAY_TIME)}
      </td>
      <td
        id={`transaction-title-${index}`}
        className={cn(CellClass, 'text-dark-navy-30 text-xs font-medium')}>
        <div className="flex items-center gap-x-2">
          <div>
            <AvatarWithIcon
              showReceiptIndicator={false}
              className="w-9 h-9 bg-white rounded-full"
              data={transaction}
              transactionStatus={transactionStatus}
            />
          </div>
          <div>{transactionTitle?.label}</div>
        </div>
      </td>
      <td id="transaction-status" className={cn(CellClass, hiddenStyleOnSmallScreens)}>
        <Capsule variant={transactionStatus} className="text-[10px] py-1" />
      </td>
      {hasGLCodes ? (
        <td
          id={`transaction-gl-code-${index}`}
          className={cn(
            CellClass,
            `${!generalLedgerCode || archived ? 'text-dark-base-90' : 'text-dark-navy-30'} text-xs font-medium`,
            hiddenStyleOnSmallScreens
          )}>
          {transaction.details?.generalLedgerCode ? (
            <Capsule
              style={{
                background:
                  !generalLedgerCode || archived
                    ? '#ECECEC'
                    : generalLedgerCode
                      ? generalLedgerCode.color
                      : transaction.details?.generalLedgerCode?.color
              }}
              customLabel={
                generalLedgerCode
                  ? `${generalLedgerCode.name} - ${generalLedgerCode.category}`
                  : `${transaction.details?.generalLedgerCode?.name} - ${transaction.details?.generalLedgerCode?.category}`
              }
            />
          ) : (
            '-'
          )}
        </td>
      ) : null}
      <td id="transaction-type" className={cn(CellClass, 'text-dark-navy-30 text-xs font-medium')}>
        <div>{getTransactionTypeLabel(transaction.type)}</div>
      </td>

      <td
        id={`transaction-amount-${index}`}
        className={cn(CellClass, 'text-dark-navy-30 text-sm font-medium')}>
        <div
          className={cn(
            isCredit(transactionAmount?.sign) ? 'text-dark-teal-30' : '',
            transactionStatus === 'declined' ? 'text-error-darkred line-through' : ''
          )}>
          <span>{getAmountSign(transactionAmount?.sign)}</span>
          <CurrencyFormat
            value={Number(transactionAmount?.amount).toFixed(2)}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            decimalScale={2}
          />
        </div>
      </td>

      <td
        id={`transaction-details-${index}`}
        className={cn(CellClass, 'text-dark-navy-30 text-xs font-medium')}>
        <div className="flex flex-col">
          <div>{transactionDetails?.label}</div>
          <div>{transactionDetails?.labelDescription}</div>
        </div>
      </td>

      <td id={`transaction-attachment-${index}`} className={cn(CellClass, 'px-8 relative')}>
        <ReceiptCell transaction={transaction} customer={customer} />
      </td>
    </tr>
  );
};

export default TransactionHistoryTableRow;
